import React, { useMemo } from "react";

import { normalizeGroupByCharacteristic } from "utils";

import { useCharacteristics, useGroupByContent } from "../../../hooks";
import { DealTableGroupByFields, ReportsQueries } from "../../../store/types";
import { AutocompleteMultiSelect, SelectOption } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";
import { AutocompleteMeasures } from "../Set-table-columns/AutocompleteMeasures";

import "./SetTableGroupBy.styles.scss";

export type DataFormation = Partial<Pick<ReportsQueries, "groupBy" | "rowBy">>;

const dealViewGroupByOptions: SelectOption<DealTableGroupByFields>[] = [
  { text: "Investor", key: "Investor", value: "investor" },
  { text: "Measure", key: "Measure", value: "measure" },
  { text: "Date", key: "Date", value: "date" }
];

interface SetTableGroupByProps {
  onChangeDataFormation?: (dataFormation: DataFormation) => void;
  isDealView?: boolean;
}

export const SetTableGroupBy: React.FC<SetTableGroupByProps> = ({ onChangeDataFormation, isDealView }) => {
  const characteristicsAll = useCharacteristics();

  const fields: Array<keyof ReportsQueries> = ["measures", "calculatedFields"];

  const { setFieldValue, values } = useGroupByContent({
    fields,
    onChangeDataFormation,
    groupByFieldName: isDealView ? "dealTableGroupBy" : "groupBy"
  });

  const groupByOptions: SelectOption[] = useMemo(() => {
    return isDealView
      ? dealViewGroupByOptions
      : [
          { text: "Investor", key: "Investor", value: "investor" },
          { text: "Deal", key: "Deal", value: "deal" },
          // { text: "Asset Class", key: "Asset Class", value: "assetClass" },
          // { text: "Sub Asset Class", key: "Sub Asset Class", value: "subAssetClass" },
          { text: "Measure", key: "Measure", value: "measure" },
          ...characteristicsAll.map((char) =>
            typeof char === "string" ? { text: char, key: char, value: normalizeGroupByCharacteristic(char) } : char
          )
        ];
  }, [characteristicsAll]);

  const effectiveFirstGroupByOptions = useMemo(() => {
    return groupByOptions.filter((option) => values.secondGroupBy !== option.value);
  }, [groupByOptions, values]);

  const effectiveSecondGroupByOptions = useMemo(() => {
    return groupByOptions.filter((option) => values.firstGroupBy !== option.value);
  }, [groupByOptions, values]);

  const [firstGroupByOption, secondGroupByOption] = useMemo(() => {
    return [
      groupByOptions.find((opt) => values.firstGroupBy === opt.value),
      groupByOptions.find((opt) => values.secondGroupBy === opt.value)
    ];
  }, [groupByOptions, values]);

  const groupByHasMeasures = values.firstGroupBy === "measure" || values.secondGroupBy === "measure";

  return (
    <div className="group-by-content">
      <div className="group-by-content__title">Select Rows</div>
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={firstGroupByOption}
            label="First Group By"
            options={effectiveFirstGroupByOptions}
            onChange={(value) => setFieldValue("firstGroupBy", value)}
          />
        </div>
      </div>
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMultiSelect
            defaultValue={secondGroupByOption}
            label="Second Group By"
            options={effectiveSecondGroupByOptions}
            onChange={(value) => setFieldValue("secondGroupBy", value)}
          />
        </div>
      </div>
      {groupByHasMeasures && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMeasures
              label="Measures"
              queries={values}
              setQueryValue={setFieldValue}
              showGroups={["measures", "calculatedFields"]}
            />
          </div>
        </div>
      )}
    </div>
  );
};
